import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { isProblemError } from "../../api/client";
import { useGetMe, useLogout } from "../../api/generated/identity/identity";
import type { Me } from "../../api/generated/model";
import { applyPersonLocale, wasLocaleChangedManually } from "../../i18n";

export type SessionState =
  | "full"
  | "mfa_enrollment_required"
  | "mfa_verification_required";

export interface Session {
  readonly me: Me | undefined;
  readonly state: SessionState | "unauthenticated";
  readonly isPending: boolean;
  readonly isUnauthenticated: boolean;
  readonly hasError: boolean;
}

function deriveState(me: Me | undefined): SessionState | "unauthenticated" {
  if (!me) return "unauthenticated";
  if (me.mfaSatisfied) return "full";
  if (me.mfaEnrolled) return "mfa_verification_required";
  return "mfa_enrollment_required";
}

export function useSession(): Session {
  const query = useGetMe();
  const me = query.data;
  const appliedFor = useRef<string | null>(null);
  const isUnauthorized = isProblemError(query.error) && query.error.status === 401;
  const activeMe = isUnauthorized ? undefined : me;

  useEffect(() => {
    if (isUnauthorized) {
      appliedFor.current = null;
      return;
    }
    if (!activeMe || wasLocaleChangedManually()) return;
    if (appliedFor.current === activeMe.id) return;
    appliedFor.current = activeMe.id;
    void applyPersonLocale(activeMe.locale);
  }, [activeMe, isUnauthorized]);

  return {
    me: activeMe,
    state: deriveState(activeMe),
    isPending: query.isPending,
    isUnauthenticated: isUnauthorized || (!query.isPending && !activeMe),
    hasError: query.isError && !isUnauthorized,
  };
}

/**
 * Sair da conta.
 *
 * **So navega quando o servidor confirmou.** Era `onSettled`, que trata falha
 * de rede, 502 do proxy e 5xx como saida bem-sucedida: a tela limpava e ia para
 * /login com o cookie `pv_session` ainda valido no servidor. Em computador
 * compartilhado, quem senta depois digita qualquer rota autenticada e cai na
 * sessao anterior — e quem "saiu" nao tem como descobrir. O cookie e `HttpOnly`
 * e nenhum JavaScript pode apaga-lo: se o POST falhou, a sessao **esta** viva, e
 * dizer o contrario e a unica coisa que a tela nao pode fazer.
 *
 * Em falha a pessoa fica onde esta com o botao habilitado para tentar de novo.
 * A frase que explica o que houve pertence a quem renderiza o botao; aqui ela
 * nao cabe, porque hook nao desenha. Quem chama observa `isError`.
 */
export function useLogoutAction() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useLogout({
    mutation: {
      onSuccess: () => {
        // `clear()` e nao `resetQueries()`: reset REFAZ toda consulta montada, e
        // neste instante o cookie ja foi revogado — a tela dispara uma rajada de
        // 401, um por consulta da pagina, e cada 401 agenda mais uma leitura de
        // /me. `clear()` descarta sem pedir nada, entao o cache nao se repovoa
        // com o dado de quem acabou de sair antes de a navegacao acontecer.
        queryClient.clear();
        void navigate("/login", { replace: true });
      },
    },
  });
}
